// BankDropdown.jsx - Updated with card type detection
// Place this in: client/src/components/common/BankDropdown.jsx

import React, { useState } from 'react';
import '../../styles/others/BankDropdown.css';

const PHILIPPINE_BANKS = [
  'BDO Unibank',
  'Bank of the Philippine Islands (BPI)',
  'Metrobank', 
  'Security Bank',
  'UnionBank of the Philippines',
  'Land Bank of the Philippines (Landbank)',
  'Philippine National Bank (PNB)',
  'Rizal Commercial Banking Corporation (RCBC)',
  'EastWest Bank',
  'China Banking Corporation (Chinabank)',
  'Maya Bank',
  'GoTyme Bank',
  'Other Bank' // This will show text input
];

// Card type detection based on account number patterns
const detectCardType = (accountNumber) => {
  const cleaned = accountNumber.replace(/\s|-/g, '');
  
  // Visa: starts with 4, length 13-19 digits
  if (/^4[0-9]{12,18}$/.test(cleaned)) {
    return 'Visa';
  }
  
  // Mastercard: starts with 5[1-5] or 2[2-7], length 16 digits
  if (/^5[1-5][0-9]{14}$/.test(cleaned) || /^2[2-7][0-9]{14}$/.test(cleaned)) {
    return 'Mastercard';
  }
  
  // American Express: starts with 34 or 37, length 15 digits
  if (/^3[47][0-9]{13}$/.test(cleaned)) {
    return 'American Express';
  }
  
  // Discover: starts with 6, length 16 digits
  if (/^6(?:011|5[0-9]{2})[0-9]{12}$/.test(cleaned)) {
    return 'Discover';
  }
  
  // JCB: starts with 35, length 16 digits
  if (/^35[0-9]{14}$/.test(cleaned)) {
    return 'JCB';
  }
  
  return null;
};

const BankDropdown = ({ 
  value, 
  onChange, 
  placeholder = "Select Bank", 
  className = "",
  disabled = false,
  error = null,
  accountNumber = '', // New prop for card detection
  onCardTypeDetected = null // Callback when card type is detected
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [customBankName, setCustomBankName] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [detectedCardType, setDetectedCardType] = useState(null);

  // Detect card type when account number changes
  React.useEffect(() => {
    if (accountNumber && accountNumber.length >= 13) {
      const cardType = detectCardType(accountNumber);
      setDetectedCardType(cardType);
      
      // Call callback if provided
      if (onCardTypeDetected) {
        onCardTypeDetected(cardType);
      }
    } else {
      setDetectedCardType(null);
      if (onCardTypeDetected) {
        onCardTypeDetected(null);
      }
    }
  }, [accountNumber, onCardTypeDetected]);

  // Filter banks based on search term
  const filteredBanks = PHILIPPINE_BANKS.filter(bank =>
    bank.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleBankSelect = (bankName) => {
    if (bankName === 'Other Bank') {
      setShowCustomInput(true);
      setIsOpen(false);
      return;
    }
    
    onChange(bankName);
    setIsOpen(false);
    setSearchTerm('');
    setShowCustomInput(false);
  };

  const handleCustomBankSubmit = () => {
    if (customBankName.trim()) {
      onChange(customBankName.trim());
      setCustomBankName('');
      setShowCustomInput(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (showCustomInput) {
        handleCustomBankSubmit();
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setShowCustomInput(false);
      setSearchTerm('');
    }
  };

  // Get card type icon
  const getCardIcon = (cardType) => {
    switch (cardType) {
      case 'Visa':
        return '💳'; // You can replace with actual Visa icon
      case 'Mastercard':
        return '💳'; // You can replace with actual Mastercard icon
      case 'American Express':
        return '💳';
      case 'Discover':
        return '💳';
      case 'JCB':
        return '💳';
      default:
        return '';
    }
  };

  return (
    <div className={`bank-dropdown-container ${className}`}>
      {/* Card Type Detection Display */}
      {detectedCardType && (
        <div className="card-type-detected">
          <span className="card-icon">{getCardIcon(detectedCardType)}</span>
          <span className="card-type-text">
            Detected: <strong>{detectedCardType}</strong>
          </span>
        </div>
      )}

      {/* Main Dropdown */}
      <div className={`bank-dropdown ${isOpen ? 'open' : ''} ${error ? 'error' : ''}`}>
        <div 
          className={`bank-dropdown-trigger ${disabled ? 'disabled' : ''}`}
          onClick={() => !disabled && setIsOpen(!isOpen)}
        >
          <span className={`bank-dropdown-value ${!value ? 'placeholder' : ''}`}>
            {value || placeholder}
          </span>
          <span className={`bank-dropdown-arrow ${isOpen ? 'up' : 'down'}`}>
            ▼
          </span>
        </div>

        {isOpen && (
          <div className="bank-dropdown-menu">
            {/* Search Input */}
            <div className="bank-dropdown-search">
              <input
                type="text"
                placeholder="Search banks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
              />
            </div>

            {/* Bank Options */}
            <div className="bank-dropdown-options">
              {filteredBanks.length > 0 ? (
                filteredBanks.map((bank, index) => (
                  <div
                    key={index}
                    className={`bank-dropdown-option ${bank === 'Other Bank' ? 'other-option' : ''}`}
                    onClick={() => handleBankSelect(bank)}
                  >
                    {bank}
                  </div>
                ))
              ) : (
                <div className="bank-dropdown-no-results">
                  No banks found matching "{searchTerm}"
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Custom Bank Input */}
      {showCustomInput && (
        <div className="bank-dropdown-custom-input">
          <input
            type="text"
            placeholder="Enter bank name..."
            value={customBankName}
            onChange={(e) => setCustomBankName(e.target.value)}
            onKeyDown={handleKeyDown}
            autoFocus
          />
          <div className="bank-dropdown-custom-actions">
            <button 
              type="button"
              onClick={handleCustomBankSubmit}
              disabled={!customBankName.trim()}
              className="add-btn"
            >
              Add
            </button>
            <button 
              type="button"
              onClick={() => {
                setShowCustomInput(false);
                setCustomBankName('');
              }}
              className="cancel-btn"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bank-dropdown-error">
          {error}
        </div>
      )}
    </div>
  );
};

export default BankDropdown;